/**
 * The four-condition matrix audit (RD-56, MIT).
 *
 * Ten Neutralization Theorems collapse to four auditable linear-algebra
 * conditions on a governance system embedded in GL(n,ℂ). This module runs the
 * four matrix operations the audit names — det / trace / matrix exponential /
 * eigenvalues — directly through the `@csk/core` Mₙ(ℂ) realization
 * (`@csk/core/linear`), and returns structured pass/fail results plus the
 * computed quantities. The audit is dimension-general: the same four operations
 * certify a 2×2 system and a 3×3 system without change.
 *
 *   A1  Authority subgroup closure   — diagonal positive ⇒ exp-stable Lie subgroup
 *   A2  Spectral separation          — eigenvalue clusters separated by a gap ε
 *   A3  Temporal trace stability     — Tr(log M) = 0  (det(M) = 1, volume-preserving)
 *   A4  Resource master identity     — exp(Tr X) = det(exp X)  (Jacobi's formula)
 *
 * The audit verifies a system is *eligible* for the structural guarantee:
 * once A1–A4 hold the ten NTs hold unconditionally, by construction — this is
 * not a runtime filter that catches attacks. React-free and deterministic.
 *
 * Because the operations are genuinely complex, A2 is correct over GL(n,ℂ): a
 * rotation block [[0,1],[-1,0]] has the conjugate eigenpair ±i, whose true
 * separation in ℂ is 2 — not the 0 a real-part-only reading would report.
 *
 * Companion to RD-56 and the MATRIX-VERIFICATION-SECURITY-GUIDE audit protocol.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import {
  cExp,
  cMod,
  cSub,
  cmatDet,
  cmatDimension,
  cmatEigenvalues,
  cmatExp,
  cmatTrace,
  toTuples,
  type Complex,
} from "@csk/core/linear";

import type { Matrix } from "../data/matrixPresets.js";
import type {
  AuditVerdict,
  ConditionResult,
  OperatingMatrices,
  SingularityReport,
} from "./types.js";

/** Numeric tolerance for the equality-style conditions (A3, A4). */
export const TOLERANCE = 1e-6;

/** Minimum eigenvalue-cluster gap for spectral separation (A2). */
export const SPECTRAL_GAP_EPSILON = 1e-3;

/** |det| threshold below which the resource block is flagged pre-singular. */
export const SINGULARITY_THRESHOLD = 0.1;

/** det(m) as a plain real number (the real part of the complex determinant). */
export function det(m: Matrix): number {
  return cmatDet(m)[0];
}

/** trace(m) as a plain real number (the real part of the complex trace). */
export function trace(m: Matrix): number {
  return cmatTrace(m)[0];
}

/** Matrix exponential exp(m), computed in GL(n,ℂ). */
export function expm(m: Matrix): Matrix {
  return cmatExp(m);
}

/**
 * The spectral gap of the knowledge operator: the minimum pairwise distance
 * minᵢ<ⱼ |λᵢ − λⱼ| between its n eigenvalues in the complex plane. (For n = 2
 * this is the single distance |λ₀ − λ₁|.) Computing this in ℂ (not between real
 * parts) is what makes A2 correct over GL(n,ℂ) — a real matrix with a
 * complex-conjugate eigenpair registers its true separation 2·|im|, not zero.
 */
export function spectralGap(m: Matrix): number {
  const eigenvalues = cmatEigenvalues(m);
  let gap = Number.POSITIVE_INFINITY;
  for (let i = 0; i < eigenvalues.length; i += 1) {
    for (let j = i + 1; j < eigenvalues.length; j += 1) {
      gap = Math.min(gap, cMod(cSub(eigenvalues[i], eigenvalues[j])));
    }
  }
  return gap;
}

/**
 * Whether an n×n complex matrix is diagonal within tolerance: every
 * off-diagonal entry has modulus below TOLERANCE.
 */
function isDiagonal(m: Matrix): boolean {
  const rows = toTuples(m);
  return rows.every((row, i) =>
    row.every((z, j) => i === j || cMod(z) <= TOLERANCE),
  );
}

/**
 * A1 — Authority subgroup closure. A diagonal matrix with strictly positive
 * diagonal is an element of the Lie subgroup {diag(a₁, …, aₙ) | aᵢ > 0}, which
 * is exp-stable and closed under product and inverse. We check that closure
 * proxy over GL(n,ℂ): the off-diagonal entries vanish (|z| < ε), every diagonal
 * entry is real and positive (re > 0, im ≈ 0), and the matrix is invertible.
 */
export function auditA1(authority: Matrix): ConditionResult {
  const diagonal = isDiagonal(authority);
  const d = cmatDet(authority);
  const rows = toTuples(authority);
  const diag: readonly Complex[] = rows.map((row, i) => row[i]);
  const positiveDiagonal = diag.every(
    (z) => z[0] > 0 && Math.abs(z[1]) <= TOLERANCE,
  );
  // Reuse the determinant `d` for the invertibility check (|det| > ε) rather
  // than recomputing it — and at the audit's own TOLERANCE, not the linear
  // module's default.
  const detMagnitude = cMod(d);
  const pass = diagonal && positiveDiagonal && detMagnitude > TOLERANCE;
  return {
    id: "A1",
    pass,
    quantity: `det = ${formatScalar(d[0])}`,
    detail: pass
      ? "Authority is a closed, exp-stable Lie subgroup element (PERMIT, COMPOSE neutralized by construction)."
      : diagonal
        ? "Authority block is not a positive-definite invertible diagonal element — subgroup closure is not established."
        : "Authority block has nonzero off-diagonal coupling — it is not closed under composition/inverse.",
    values: { det: d[0], diagonal: diagonal ? 1 : 0 },
  };
}

/**
 * A2 — Spectral separation. The knowledge operator's eigenvalue clusters must
 * be separated by a gap ≥ ε, so each information domain is an independent
 * eigenspace and the off-diagonal blocks vanish. The gap is the minimum
 * pairwise eigenvalue distance, computed in ℂ, so a rotation-block knowledge
 * matrix with a conjugate eigenpair reports its true separation rather than
 * collapsing to zero.
 */
export function auditA2(knowledge: Matrix): ConditionResult {
  const gap = spectralGap(knowledge);
  const pass = gap >= SPECTRAL_GAP_EPSILON;
  return {
    id: "A2",
    pass,
    quantity: `gap = ${formatScalar(gap)}`,
    detail: pass
      ? `Eigenvalue clusters are separated by ${formatScalar(gap)} ≥ ε in ℂ — domains are independent eigenspaces (SEPARATE, DATA.ORIGIN, DATA.REACH neutralized).`
      : `Eigenvalue clusters overlap (gap ${formatScalar(gap)} < ε in ℂ) — a cross-domain data path may exist.`,
    // An n×n operator has n eigenvalues; read n from the shape rather than
    // running the eigensolver a second time (spectralGap already ran it).
    values: { gap, eigenvalueCount: cmatDimension(knowledge) },
  };
}

/**
 * A3 — Temporal trace stability. A temporal transition M is volume-preserving
 * exactly when Tr(log M) = 0, equivalently det(M) = 1. We read Tr(log M) off
 * the determinant: Tr(log M) = log(det M), taking the real part of the complex
 * determinant.
 */
export function auditA3(temporal: Matrix): ConditionResult {
  const d = cmatDet(temporal);
  const reDet = d[0];
  const traceLog = reDet > 0 ? Math.log(reDet) : Number.NaN;
  const pass = Number.isFinite(traceLog) && Math.abs(traceLog) <= TOLERANCE;
  return {
    id: "A3",
    pass,
    quantity: `Tr(log M) = ${formatScalar(traceLog)}`,
    detail: pass
      ? "Tr(log M) = 0 — the temporal transition is volume-preserving (det M = 1); PRECOMMIT and the invariant theorems are neutralized."
      : Number.isFinite(traceLog)
        ? `Tr(log M) = ${formatScalar(traceLog)} ≠ 0 — the temporal flow is not volume-preserving; a temporal-bypass / replay path may exist.`
        : "det(M) ≤ 0 — the temporal transition is non-invertible; temporal flow has left GL(n).",
    values: { traceLog, detM: reDet },
  };
}

/**
 * A4 — Resource master identity. Jacobi's formula gives exp(Tr X) = det(exp X)
 * for every matrix X; we run both limbs live in ℂ and report the residual. A
 * well-formed resource generator passes within tolerance; a large residual
 * signals a resource inflation/leakage path.
 */
export function auditA4(resourceGenerator: Matrix): ConditionResult {
  const predicted: Complex = cExp(cmatTrace(resourceGenerator));
  const observed: Complex = cmatDet(cmatExp(resourceGenerator));
  const residual = cMod(cSub(predicted, observed));
  const predictedDet = cMod(predicted);
  const observedDet = cMod(observed);
  const traceX = cmatTrace(resourceGenerator)[0];
  // Compare on a relative scale so tiny dets (pre-singular) still pass cleanly.
  const scale = Math.max(1, predictedDet, observedDet);
  const pass = residual / scale <= TOLERANCE;
  return {
    id: "A4",
    pass,
    quantity: `|residual| = ${formatScalar(residual)}`,
    detail: pass
      ? "exp(Tr X) = det(exp X) within tolerance — the additive and multiplicative resource accounts agree (RES.CONSUME, RES.RELEASE neutralized)."
      : `exp(Tr X) ≠ det(exp X) (residual ${formatScalar(residual)}) — a resource inflation or leakage path may exist.`,
    values: { traceX, predictedDet, observedDet, residual },
  };
}

/**
 * Pre-singularity reading on the resource block exp(X). As |det| → 0 the
 * system approaches M(n) \\ GL(n), where the resource obligation becomes
 * unresolvable; the warning fires before the guarantee lapses (RD-56 §7).
 */
export function nearSingular(resourceGenerator: Matrix): SingularityReport {
  const detMagnitude = cMod(cmatDet(cmatExp(resourceGenerator)));
  const status: SingularityReport["status"] =
    detMagnitude >= 1 - TOLERANCE
      ? "nominal"
      : detMagnitude >= SINGULARITY_THRESHOLD
        ? "elevated"
        : detMagnitude >= 0.01
          ? "warning"
          : "critical";
  return {
    detMagnitude,
    nearSingular: detMagnitude < SINGULARITY_THRESHOLD,
    status,
  };
}

/** Run all four conditions and assemble the immunity verdict. */
export function auditAll(matrices: OperatingMatrices): AuditVerdict {
  const conditions: [
    ConditionResult,
    ConditionResult,
    ConditionResult,
    ConditionResult,
  ] = [
    auditA1(matrices.authority),
    auditA2(matrices.knowledge),
    auditA3(matrices.temporal),
    auditA4(matrices.resourceGenerator),
  ];
  const failing = conditions.filter((c) => !c.pass).map((c) => c.id);
  return {
    conditions,
    immune: failing.length === 0,
    failing,
    singularity: nearSingular(matrices.resourceGenerator),
  };
}

/** Compact scalar formatter shared by the audit detail strings. */
export function formatScalar(value: number): string {
  if (Number.isNaN(value)) return "NaN";
  if (!Number.isFinite(value)) return value > 0 ? "+∞" : "−∞";
  if (value === 0) return "0";
  if (Math.abs(value) >= 1e5 || Math.abs(value) < 1e-3) {
    return value.toExponential(3);
  }
  return value.toFixed(4);
}
